import React from "react";
import { Helmet } from "react-helmet-async";
import Breadcrumbs from "../components/Breadcrumbs";
import { staticMeta } from "../lib/seo";

/**
 * /about — plain-language explainer of what CardCapture is.
 * Per owner spec (SEO pass §7): community reference library, real
 * photos and videos, texture/holo/glitter that flat scans miss,
 * visual-reference-only, fan-project disclaimer.
 */
export default function AboutPage() {
  const meta = staticMeta("about");
  return (
    <div
      className="mx-auto w-full max-w-[900px] px-5 py-10 md:px-gutter md:py-14"
      data-testid="about-page"
    >
      <Helmet>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <meta name="robots" content={meta.robots} />
        <link rel="canonical" href={meta.canonical} />
        <meta property="og:site_name" content="CardCapture" />
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content={meta.ogType} />
        <meta property="og:image" content={meta.ogImage} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={meta.title} />
        <meta name="twitter:description" content={meta.description} />
        <meta name="twitter:image" content={meta.ogImage} />
      </Helmet>

      <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "About" }]} />

      <div className="mt-6 text-eyebrow uppercase text-indigo-text">
        About
      </div>
      <h1 className="mt-1 text-title-page text-ink-text">
        A visual reference library for trading cards
      </h1>

      <div className="mt-6 flex flex-col gap-6 text-body text-ink-muted">
        <p>
          CardCapture is a community reference library for TCG collectors.
          Every card has its own page. Alongside the official reference
          image, collectors upload photos and short videos of the exact
          printing they own — showing texture, holo pattern, glitter, and
          finish that flat scanner images cannot capture.
        </p>
        <p>
          Approved submissions are ranked using a Wilson score lower
          confidence bound, so cards with a handful of strong photos
          surface above cards with many mediocre ones. V1 covers English
          Pokémon; more games and languages will follow.
        </p>
        <p>
          CardCapture is a visual reference tool. It does not sell cards,
          it does not grade cards, and it does not list them for sale.
          Market prices shown on card pages are attribution-only imports
          from public data sources and are not offers.
        </p>
        <p data-testid="about-fan-disclaimer" className="text-ink-text">
          CardCapture is an unofficial fan project. It is not affiliated
          with, endorsed by, or sponsored by The Pokémon Company,
          Nintendo, Creatures, or GAME FREAK. Pokémon and all associated
          names are trademarks of their respective owners.
        </p>
      </div>
    </div>
  );
}
